import axios from 'axios';

// Always use relative '/api' - works on preview, deployed, and custom domains
// Kubernetes ingress routes /api to the backend automatically
const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Add token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/') &&
      // Don't try to refresh if there's no refresh token — that just causes
      // a 401→refresh→redirect loop on public/unauthenticated pages.
      localStorage.getItem('refresh_token') &&
      // Don't force redirects on public routes that don't need auth.
      !/^\/(v|view|sign|portal|login|dispatch)(\/|$)/.test(window.location.pathname)
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const { data } = await api.post('/auth/refresh', { refresh_token: refreshToken });
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
        }
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Only redirect if we're not already on a public route
        if (!/^\/(v|view|sign|portal|login|dispatch)(\/|$)/.test(window.location.pathname)) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
    // Read-only mode: the backend's subscription guard returned 403 with
    // reason=subscription_canceled. Surface a friendly toast so the user
    // understands why "Save" silently failed — but only once per minute
    // to avoid spamming on retry-happy components.
    if (
      error.response?.status === 403 &&
      error.response?.data?.reason === 'subscription_canceled'
    ) {
      try {
        const last = window.__mantleLastReadOnlyToast || 0;
        const now = Date.now();
        if (now - last > 60000) {
          window.__mantleLastReadOnlyToast = now;
          const { toast } = await import('sonner');
          toast.error('Your subscription is canceled — editing is disabled. Reactivate from Settings → Billing to continue.', { duration: 8000 });
        }
      } catch { /* toast import failure shouldn't break the flow */ }
    }
    // Plan-gate 403 (feature not on the workspace's current plan). The
    // backend produces two shapes for plan gates:
    //   • plan_gate middleware  → {reason:'feature_not_in_plan', feature, plan}
    //   • feature_required dep  → {detail:{reason:'feature_not_in_plan', ...}}
    // We check both. Only surface a toast on user-initiated mutations
    // (POST/PUT/PATCH/DELETE) — silent for background GETs so pages
    // don't nag the user.
    const d = error.response?.data;
    const planGateReason = d?.reason || d?.detail?.reason;
    if (
      error.response?.status === 403 &&
      planGateReason === 'feature_not_in_plan' &&
      !['get', 'head', 'options'].includes((originalRequest?.method || '').toLowerCase())
    ) {
      try {
        const last = window.__mantleLastPlanGateToast || 0;
        const now = Date.now();
        if (now - last > 60000) {
          window.__mantleLastPlanGateToast = now;
          const { toast } = await import('sonner');
          const msg = d?.message || d?.detail?.message || d?.detail || 'This feature is not on your current plan.';
          toast.error(typeof msg === 'string' ? msg : 'This feature is not on your current plan.', {
            duration: 8000,
            action: {
              label: 'Upgrade',
              onClick: () => { window.location.href = '/settings?section=billing'; },
            },
          });
        }
      } catch { /* */ }
    }
    return Promise.reject(error);
  }
);

export default api;
